// 📄 pages/MiEspacio/sections/SnippetsSection.tsx
import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Plus, Trash2, Pencil, Copy, Check, Code2, X } from 'lucide-react';
import { useAuth } from '../../../context/AuthContext.tsx';
import { Snippet, SnippetData, LENGUAJES_SNIPPET } from '../../../types/miEspacio.ts';
import { getSnippets, saveSnippet, deleteSnippet } from '../../../services/miEspacioService.ts';
import ConfirmDialog from '../../../components/ui/ConfirmDialog.tsx';
import { MiEspacioSeccionProps, etiquetaContexto } from '../miEspacioContexto.ts';

const inputClass = "w-full bg-zinc-800 border border-white/10 rounded-lg p-2.5 text-sm text-white focus:outline-none focus:border-blue-500";
const labelClass = "block text-xs font-medium text-gray-400 mb-1";

const emptyForm: SnippetData = { titulo: '', lenguaje: '', tags: [], codigo: '', descripcion: '', ubicacionTecnica: '' };

const ALTURA_COLAPSADA = 120;

// Bloque de código de un snippet, con altura acotada por defecto y botón
// "Ver más"/"Colapsar" cuando el contenido desborda esos 120px — estado
// local (useState), no persiste en Firestore. `desborda` se mide una sola
// vez al montar/cambiar el código, comparando scrollHeight (alto real del
// contenido) contra clientHeight (alto visible con el recorte aplicado):
// si son iguales, el código ya cabía y no hace falta mostrar el botón.
function CodigoColapsable({ codigo }: { codigo: string }) {
  const [expandido, setExpandido] = useState(false);
  const [desborda, setDesborda] = useState(false);
  const preRef = useRef<HTMLPreElement>(null);

  useLayoutEffect(() => {
    const el = preRef.current;
    if (!el) return;
    setDesborda(el.scrollHeight > el.clientHeight + 1);
  }, [codigo]);

  return (
    <div className="relative">
      <pre
        ref={preRef}
        className="bg-black/30 rounded-lg p-3 text-xs text-gray-300 font-mono overflow-hidden whitespace-pre-wrap"
        style={{ maxHeight: expandido ? undefined : ALTURA_COLAPSADA }}
      >
        {codigo}
      </pre>

      {!expandido && desborda && (
        <div className="absolute inset-x-0 bottom-0 h-10 rounded-b-lg bg-gradient-to-t from-black/80 via-black/40 to-transparent pointer-events-none" />
      )}

      {desborda && (
        <button
          type="button"
          onClick={() => setExpandido((v) => !v)}
          className="absolute bottom-1.5 left-1/2 -translate-x-1/2 flex items-center gap-1 text-[11px] font-semibold text-blue-300 hover:text-blue-200 bg-zinc-800/90 border border-white/10 px-2.5 py-1 rounded-full shadow-lg transition-colors"
        >
          {expandido ? '▲ Colapsar' : '▼ Ver más'}
        </button>
      )}
    </div>
  );
}

export default function SnippetsSection({ contextosConsulta, contextoNuevo, proyectosPorId, mostrarProyecto }: MiEspacioSeccionProps) {
  const { user } = useAuth();
  const [snippets, setSnippets] = useState<Snippet[]>([]);
  const [loading, setLoading] = useState(true);
  const [formOpen, setFormOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editingProjectId, setEditingProjectId] = useState<string | null>(null);
  const [form, setForm] = useState<SnippetData>(emptyForm);
  const [tagsInput, setTagsInput] = useState('');
  const [guardando, setGuardando] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [aEliminar, setAEliminar] = useState<Snippet | null>(null);
  const [copiedId, setCopiedId] = useState<string | null>(null);

  const cargar = async () => {
    if (!user) return;
    setLoading(true);
    try {
      setSnippets(await getSnippets(user.uid, contextosConsulta));
    } catch (err) {
      console.error('Error cargando snippets:', err);
    } finally {
      setLoading(false);
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { cargar(); }, [user?.uid, contextosConsulta.join(',')]);

  const openCreate = () => {
    setEditingId(null);
    setEditingProjectId(contextoNuevo);
    setForm(emptyForm);
    setTagsInput('');
    setError(null);
    setFormOpen(true);
  };

  const openEdit = (snippet: Snippet) => {
    setEditingId(snippet.id);
    setEditingProjectId(snippet.projectId);
    setForm({
      titulo: snippet.titulo, lenguaje: snippet.lenguaje, tags: snippet.tags, codigo: snippet.codigo,
      descripcion: snippet.descripcion, ubicacionTecnica: snippet.ubicacionTecnica || ''
    });
    setTagsInput(snippet.tags.join(', '));
    setError(null);
    setFormOpen(true);
  };

  const handleGuardar = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user) return;
    setGuardando(true);
    setError(null);
    try {
      const tags = tagsInput.split(',').map((t) => t.trim()).filter(Boolean);
      await saveSnippet(user.uid, editingId, { ...form, tags }, editingProjectId);
      setFormOpen(false);
      cargar();
    } catch (err: any) {
      setError(err.message || 'Error al guardar el snippet.');
    } finally {
      setGuardando(false);
    }
  };

  const handleEliminar = async () => {
    if (!user || !aEliminar) return;
    try {
      await deleteSnippet(user.uid, aEliminar.id, aEliminar.projectId);
      setAEliminar(null);
      cargar();
    } catch (err) {
      console.error('Error al eliminar snippet:', err);
    }
  };

  const handleCopiar = async (snippet: Snippet) => {
    await navigator.clipboard.writeText(snippet.codigo);
    setCopiedId(snippet.id);
    setTimeout(() => setCopiedId(null), 1500);
  };

  return (
    <div className="space-y-4">
      <div className="flex justify-end">
        {!formOpen && (
          <button onClick={openCreate} className="flex items-center gap-2 bg-blue-600 hover:bg-blue-500 px-4 py-2.5 rounded-lg text-sm font-semibold text-white transition-all">
            <Plus className="w-4 h-4" /> Nuevo snippet
          </button>
        )}
      </div>

      {formOpen && (
        <form onSubmit={handleGuardar} className="bg-white/5 border border-white/10 rounded-2xl p-5 space-y-4">
          <div className="flex items-center justify-between">
            <h2 className="text-sm font-semibold text-gray-300 flex items-center gap-2">
              <Code2 className="w-4 h-4 text-blue-400" /> {editingId ? 'Editar snippet' : 'Nuevo snippet'}
            </h2>
            <button type="button" onClick={() => setFormOpen(false)} className="text-gray-500 hover:text-white">
              <X className="w-4 h-4" />
            </button>
          </div>

          {error && <div className="bg-red-500/10 border border-red-500/30 text-red-400 p-3 rounded-lg text-xs">{error}</div>}

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className={labelClass}>Título</label>
              <input className={inputClass} value={form.titulo} onChange={(e) => setForm({ ...form, titulo: e.target.value })} placeholder="Ej: Mapping CIG para ASN" />
            </div>
            <div>
              <label className={labelClass}>Lenguaje / Tipo</label>
              <select className={inputClass} value={form.lenguaje} onChange={(e) => setForm({ ...form, lenguaje: e.target.value })}>
                <option value="">Sin especificar</option>
                {/* Campo históricamente libre — si el snippet ya tenía un
                    valor que no está en la lista fija, se inyecta como
                    opción extra para no perderlo/forzarlo al guardar. */}
                {form.lenguaje && !LENGUAJES_SNIPPET.includes(form.lenguaje) && (
                  <option value={form.lenguaje}>{form.lenguaje}</option>
                )}
                {LENGUAJES_SNIPPET.map((l) => <option key={l} value={l}>{l}</option>)}
              </select>
            </div>
          </div>

          <div>
            <label className={labelClass}>Tags / Etiquetas (separadas por coma)</label>
            <input className={inputClass} value={tagsInput} onChange={(e) => setTagsInput(e.target.value)} placeholder="ABAP, CIG" />
          </div>

          <div>
            <label className={labelClass}>Ubicación técnica (opcional)</label>
            <input
              className={inputClass}
              value={form.ubicacionTecnica || ''}
              onChange={(e) => setForm({ ...form, ubicacionTecnica: e.target.value })}
              placeholder="Ej: BAdI ARBCIG_ERP_SES_CREATE, Programa ZREP_ARBCIG, User Exit MV45AFZZ..."
            />
            <p className="text-[11px] text-gray-500 mt-1">En qué BAdI, programa/include o User Exit vive este código.</p>
          </div>

          <div>
            <label className={labelClass}>Fragmento de código</label>
            <textarea className={`${inputClass} font-mono min-h-[160px]`} value={form.codigo} onChange={(e) => setForm({ ...form, codigo: e.target.value })} placeholder="Pega aquí tu código..." />
          </div>

          <div>
            <label className={labelClass}>Descripción</label>
            <textarea className={`${inputClass} min-h-[70px]`} value={form.descripcion} onChange={(e) => setForm({ ...form, descripcion: e.target.value })} />
          </div>

          <div className="flex justify-end">
            <button type="submit" disabled={guardando} className="bg-blue-600 hover:bg-blue-500 disabled:bg-gray-600 px-5 py-2.5 rounded-lg text-sm font-semibold text-white transition-all">
              {guardando ? 'Guardando...' : 'Guardar snippet'}
            </button>
          </div>
        </form>
      )}

      {loading ? (
        <p className="text-sm text-gray-400">Cargando snippets...</p>
      ) : snippets.length === 0 ? (
        <div className="p-10 text-center text-gray-500 bg-white/5 border border-white/10 rounded-2xl">
          <Code2 className="w-8 h-8 mx-auto mb-2 text-gray-600" />
          <p className="text-sm">Aún no tienes snippets guardados.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {snippets.map((snippet) => (
            <div key={snippet.id} className="bg-white/5 border border-white/10 rounded-2xl p-4 space-y-2">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <h3 className="text-sm font-semibold text-white">{snippet.titulo}</h3>
                  <div className="flex items-center gap-1.5 mt-1 flex-wrap">
                    <p className="text-xs text-gray-500">{snippet.lenguaje}</p>
                    {snippet.ubicacionTecnica && (
                      <span className="text-[10px] font-mono px-2 py-0.5 rounded-full bg-purple-500/10 text-purple-300 border border-purple-500/20">
                        {snippet.ubicacionTecnica}
                      </span>
                    )}
                    {mostrarProyecto && (
                      <span className="text-[10px] px-2 py-0.5 rounded-full bg-indigo-500/10 text-indigo-300 border border-indigo-500/20">
                        {etiquetaContexto(snippet.projectId, proyectosPorId)}
                      </span>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-1.5 flex-shrink-0">
                  <button onClick={() => handleCopiar(snippet)} className="p-1.5 rounded-lg text-gray-400 hover:text-white hover:bg-white/10" title="Copiar código">
                    {copiedId === snippet.id ? <Check className="w-4 h-4 text-emerald-400" /> : <Copy className="w-4 h-4" />}
                  </button>
                  <button onClick={() => openEdit(snippet)} className="p-1.5 rounded-lg text-gray-400 hover:text-white hover:bg-white/10">
                    <Pencil className="w-4 h-4" />
                  </button>
                  <button onClick={() => setAEliminar(snippet)} className="p-1.5 rounded-lg text-gray-400 hover:text-red-400 hover:bg-red-500/10">
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
              {snippet.tags.length > 0 && (
                <div className="flex gap-1.5 flex-wrap">
                  {snippet.tags.map((tag) => (
                    <span key={tag} className="text-[10px] px-2 py-0.5 rounded-full bg-blue-500/10 text-blue-300 border border-blue-500/20">{tag}</span>
                  ))}
                </div>
              )}
              <CodigoColapsable codigo={snippet.codigo} />
              {snippet.descripcion && <p className="text-xs text-gray-400">{snippet.descripcion}</p>}
            </div>
          ))}
        </div>
      )}

      {aEliminar && (
        <ConfirmDialog
          title="¿Eliminar este snippet?"
          message={`"${aEliminar.titulo}" se eliminará permanentemente.`}
          confirmLabel="Eliminar"
          danger
          onConfirm={handleEliminar}
          onCancel={() => setAEliminar(null)}
        />
      )}
    </div>
  );
}
